import { settings, type EffectsLevel, type ShakeLevel } from './settings';

/**
 * Combat-readability tuning (docs/12 E1-E5). Pure visuals — nothing here
 * changes hitboxes, damage or the economy.
 */
export const FX = {
  damageNumbers: {
    /** A hit within this gap of the previous one adds into the same total (s) */
    mergeGap: 0.25,
    /** A total stops accumulating after this long even under constant fire (s) */
    mergeMax: 0.6,
    /** Hits whose anchor lands within this distance count as the same target */
    mergeRadius: 64,
    /** Float + fade time after the total seals (s) */
    lifetime: 0.6,
    /** Max numbers alive at once (normal + crit + hurt streams) */
    poolSize: 16,
    /** Merged total re-renders its text at most this often (s) */
    textRefresh: 0.05,
    riseSpeed: 90,
    riseDamping: 2.5,
    /** Fade starts when this fraction of the lifetime is left */
    fadeFrom: 0.45,
    /** Anchor lift above the target's top edge (px) */
    lift: 6,
    /** Extra lift for the -N over the ship */
    hurtLift: 12,
    fontSize: 18,
    normalScale: 1,
    critScale: 1.45,
    hurtScale: 1.25,
    /** Scale kick each time a hit merges in; decays back quickly */
    mergePop: 0.18,
    popDecay: 14,
  },

  bullets: {
    /** Friendly projectiles are additive — keep them dimmer than enemy shots */
    friendlyAlpha: 0.6,
    /** Friendly sprites draw slightly under their hitbox ... */
    friendlyVisualScale: 0.85,
    /** ... and never larger than this radius (caps supershot/plasma blobs) */
    friendlyMaxVisualRadius: 10,
    /** Sprite height = hitbox diameter × this (enemy shots match their hitbox) */
    hitboxVisualRatio: 1.15,
    friendlyPrealloc: 160,
    enemyPrealloc: 224,
  },

  explosions: {
    /** Pooled explosion sprites (hard cap is per effects level, below) */
    poolSize: 32,
    animationSpeed: 0.3,
    hurtAnimationSpeed: 0.45,
    /** Boss/player death chain: offsets (px), scales and delays (ms) */
    chain: [
      { dx: 0, dy: 0, scale: 2.2, delay: 0 },
      { dx: 40, dy: -30, scale: 1.4, delay: 120 },
      { dx: -35, dy: 25, scale: 1.6, delay: 260 },
      { dx: 0, dy: 0, scale: 2.8, delay: 420 },
    ],
  },
} as const;

export interface EffectsProfile {
  /** Explosion sprite scale multiplier */
  blastScale: number;
  /** Max explosions alive at once — extra requests are dropped */
  maxBlasts: number;
  /** How many steps of FX.explosions.chain a death chain plays */
  chainSteps: number;
  /** Boss body scale on the hit-flash frame (1 = no punch) */
  bossPunch: number;
  /** Visual scale of the small hit-drip gems (pickup radius unchanged) */
  dripScale: number;
  /** Hurt ring scale around the ship */
  hurtScale: number;
}

/** Effects intensity presets (settings.effects). HIGH = the pre-E5 look. */
export const EFFECTS: Readonly<Record<EffectsLevel, EffectsProfile>> = {
  low: { blastScale: 0.6, maxBlasts: 6, chainSteps: 2, bossPunch: 1, dripScale: 0.75, hurtScale: 0.8 },
  medium: { blastScale: 0.8, maxBlasts: 12, chainSteps: 3, bossPunch: 1.02, dripScale: 0.9, hurtScale: 1 },
  high: { blastScale: 1, maxBlasts: 32, chainSteps: 4, bossPunch: 1.05, dripScale: 1, hurtScale: 1.15 },
};

/** Screen-shake multiplier per setting, applied inside Camera.shake. */
export const SHAKE_SCALE: Readonly<Record<ShakeLevel, number>> = {
  off: 0,
  low: 0.4,
  full: 1,
};

export function effectsProfile(): EffectsProfile {
  return EFFECTS[settings.effects] ?? EFFECTS.medium;
}

export function shakeScale(): number {
  return SHAKE_SCALE[settings.screenShake] ?? 1;
}
